// Action Items — standalone tasks (no project) with full task capability:
// multi-assignee, status, priority, dates, description, subtasks, comments,
// reactions. Private: you see items you created or are assigned to (admins see
// all). They reuse the task engine; the detail/edit drawer is the shared
// TaskDrawer in `isActionItem` mode. Assigned items also appear in My Work.
//
// Two views (toggle persisted per browser): a List (table) and a Board (Kanban
// grouped by task status — drag a card onto a column to change its status_code,
// reusing the project board's `.board-*` styles).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  createActionItem,
  deleteTask,
  getActionItemCategories,
  listActionItems,
  listAssignees,
  listDepartments,
  listTaskStatuses,
  updateTask,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useTaskPriorities as usePriorities, type PriorityIndex } from "../lib/priorities";
import { formatDate } from "../lib/format";
import { TASK_STATUSES } from "../lib/constants";
import type { CreateActionItemInput, TaskRow } from "../lib/types";
import { PageBar } from "../components/Layout";
import { PriorityBadge, StatusBadge } from "../components/Badge";
import { Avatar } from "../components/Avatar";
import { Modal } from "../components/Modal";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { AssigneesField, type AssigneePick } from "../components/PersonPickers";
import { TaskDrawer } from "../components/TaskDrawer";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";

const ACTION_ITEM_SORT: Record<string, SortAccessor<TaskRow>> = {
  title: (t) => t.title,
  requester: (t) => t.created_by_name,
  status: (t) => t.status,
  due: (t) => t.due_date,
};

/** yyyy-mm-dd <input type=date> → unix seconds (local start-of-day), or null. */
function dateInputToUnix(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(new Date(y, m - 1, d).getTime() / 1000);
}

/** List (table) ↔ Board (Kanban grouped by status). Persisted per browser. */
type ActionItemView = "list" | "board";

export function ActionItemsPage() {
  const { data, loading, error, reload } = useApi((signal) => listActionItems(signal), []);
  const priorities = usePriorities();
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<ActionItemView>(
    () => (localStorage.getItem("ac.actionItems.view") === "board" ? "board" : "list"),
  );
  // Deep-link: /action-items?open=<id> opens the drawer (used from My Work).
  const [params, setParams] = useSearchParams();
  const openId = params.get("open");

  const items = data?.tasks ?? [];
  const { rows: sortedItems, sort, toggle } = useTableSort(items, ACTION_ITEM_SORT);

  // ----- Bulk selection (mirror the ticket inbox): multi-select → set status /
  // reassign / delete the selected action items in one go. -----
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const statusesApi = useApi((signal) => listTaskStatuses(signal), []);
  const rosterApi = useApi((signal) => listAssignees(signal, { workType: "action_items", field: "assignee" }), []);
  // Drop selections that no longer exist after a reload.
  useEffect(() => {
    setSelectedIds((prev) => {
      const live = new Set(items.map((t) => t.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runBulk(fn: (id: string) => Promise<unknown>) {
    if (selectedIds.size === 0) return;
    setBulkPending(true);
    setBulkError(null);
    try {
      const ids = [...selectedIds];
      const results = await Promise.allSettled(ids.map(fn));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) setBulkError(`${failed} of ${ids.length} updates failed.`);
      setSelectedIds(new Set());
      reload();
    } finally {
      setBulkPending(false);
    }
  }
  const applyStatus = (code: string) => runBulk((id) => updateTask(id, { status_code: code }));
  const applyAssignee = (userId: string) => runBulk((id) => updateTask(id, { assignee_ids: userId ? [userId] : [] }));
  async function bulkDelete() {
    const ok = await confirmDialog({
      title: "Delete action items",
      message: `Delete ${selectedIds.size} action item${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (ok) await runBulk((id) => deleteTask(id));
  }

  function open(id: string) {
    setParams({ open: id });
  }
  function closeDrawer() {
    setParams({});
  }
  function chooseView(v: ActionItemView) {
    setView(v);
    localStorage.setItem("ac.actionItems.view", v);
  }

  return (
    <>
      <PageBar
        crumbs="Action Items"
        actions={
          <>
            <div className="seg" role="group" aria-label="Action item view">
              <button
                type="button"
                className={`seg-btn${view === "list" ? " active" : ""}`}
                aria-pressed={view === "list"}
                onClick={() => chooseView("list")}
              >
                List
              </button>
              <button
                type="button"
                className={`seg-btn${view === "board" ? " active" : ""}`}
                aria-pressed={view === "board"}
                onClick={() => chooseView("board")}
              >
                Board
              </button>
            </div>
            <button className="btn primary" onClick={() => setCreating(true)}>
              + New action item
            </button>
          </>
        }
      />
      <div className={view === "board" ? "content board-content" : "content"}>
        <div className="page-head">
          <div>
            <h1>Action Items</h1>
            <div className="sub">
              Standalone to-dos assigned to people — not tied to a project. You see items you
              created or are assigned to.
            </div>
          </div>
          <button className="btn small" onClick={reload} disabled={loading}>
            Refresh
          </button>
        </div>

        {loading ? (
          <Loading label="Loading action items…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : view === "board" ? (
          <ActionItemsBoard items={items} priorities={priorities} onOpen={open} onChanged={reload} />
        ) : (
          <div className="card">
            <div className="card-head">
              <h3>Action items</h3>
              <span className="faint">{items.length}</span>
            </div>
            {selectedIds.size > 0 ? (
              <div className="bulk-bar" role="region" aria-label="Bulk actions">
                <label className="bulk-all">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selectedIds.size === items.length}
                    onChange={(e) =>
                      setSelectedIds(e.target.checked ? new Set(items.filter((t) => t.can_edit !== false).map((t) => t.id)) : new Set())
                    }
                    aria-label="Select all action items"
                  />
                  <strong>{selectedIds.size}</strong> selected
                </label>
                <select
                  className="inbox-priority"
                  disabled={bulkPending}
                  value=""
                  aria-label="Set status for selected"
                  onChange={(e) => {
                    if (e.target.value) applyStatus(e.target.value);
                  }}
                >
                  <option value="">Set status…</option>
                  {(statusesApi.data?.task_statuses ?? []).map((s) => (
                    <option key={s.id} value={s.code}>{s.name}</option>
                  ))}
                </select>
                <select
                  className="inbox-priority"
                  disabled={bulkPending}
                  value=""
                  aria-label="Assign selected"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    applyAssignee(e.target.value === "__unassign__" ? "" : e.target.value);
                  }}
                >
                  <option value="">Assign to…</option>
                  <option value="__unassign__">— Unassigned —</option>
                  {(rosterApi.data?.assignees ?? []).map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <button className="btn small danger" disabled={bulkPending} onClick={bulkDelete}>
                  Delete
                </button>
                {bulkPending ? <Spinner /> : null}
                {bulkError ? <span className="bulk-error">{bulkError}</span> : null}
                <button
                  className="btn ghost small"
                  disabled={bulkPending}
                  onClick={() => setSelectedIds(new Set())}
                  style={{ marginLeft: "auto" }}
                >
                  Clear
                </button>
              </div>
            ) : null}
            <div className="card-body tight">
              {items.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <EmptyState>No action items yet. Create one and assign it to a teammate.</EmptyState>
                </div>
              ) : (
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} aria-label="Select" />
                      <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
                      <SortTh field="requester" sort={sort} onSort={toggle}>Requester</SortTh>
                      <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                      <th>Priority</th>
                      <SortTh field="due" sort={sort} onSort={toggle}>Due</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((t) => {
                      const pr = priorities.byId(t.priority_id);
                      const readOnly = t.can_edit === false;
                      return (
                        <tr key={t.id} className="clickable" onClick={() => open(t.id)}>
                          <td onClick={(e) => e.stopPropagation()}>
                            {readOnly ? null : (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(t.id)}
                                onChange={() => toggleSelect(t.id)}
                                aria-label={`Select ${t.title}`}
                              />
                            )}
                          </td>
                          <td>
                            <button className="task-title-link" onClick={() => open(t.id)}>
                              {t.title}
                            </button>
                            {readOnly ? (
                              <span className="pill" title="Shared with you — read-only" style={{ marginLeft: 6 }}>
                                Read-only
                              </span>
                            ) : null}
                          </td>
                          <td>{t.created_by_name ?? "—"}</td>
                          <td>{t.status ? <StatusBadge code={t.status} /> : "—"}</td>
                          <td>{pr ? <PriorityBadge code={pr.code} color={pr.color ?? null} /> : "—"}</td>
                          <td>{t.due_date ? formatDate(t.due_date) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {creating ? (
        <CreateActionItemModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            reload();
            open(id);
          }}
        />
      ) : null}

      {openId ? (
        <TaskDrawer
          taskId={openId}
          isActionItem
          onClose={closeDrawer}
          onChanged={reload}
        />
      ) : null}
    </>
  );
}

function CreateActionItemModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const priorities = usePriorities();
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "action_items", field: "assignee" }), []);
  const roster = assigneesApi.data?.assignees ?? [];
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const departmentOptions = departmentsApi.data?.departments ?? [];
  const categoriesApi = useApi((signal) => getActionItemCategories(signal), []);
  const categoryOptions = categoriesApi.data?.categories ?? [];

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignees, setAssignees] = useState<AssigneePick[]>([]);
  const [priorityId, setPriorityId] = useState("");
  const [department, setDepartment] = useState("");
  const [category, setCategory] = useState("");
  const [dueDate, setDueDate] = useState("");
  const { run, pending, error } = useMutation(createActionItem);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const input: CreateActionItemInput = {
      title: title.trim(),
      ...(richTextIsEmpty(description) ? {} : { description }),
      ...(assignees.length ? { assignee_ids: assignees.map((a) => a.user_id) } : {}),
      ...(priorityId ? { priority_id: priorityId } : {}),
      ...(department ? { department } : {}),
      ...(category ? { category } : {}),
      ...(dueDate ? { due_date: dateInputToUnix(dueDate) } : {}),
    };
    const res = await run(input);
    if (res) onCreated(res.task.id);
  }

  return (
    <Modal
      title="New action item"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-action-item-form"
            disabled={pending || !title.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-action-item-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            autoFocus
            required
          />
        </label>

        <div className="field">
          <span className="label">Description</span>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Add details, context, or acceptance criteria…"
          />
        </div>

        <AssigneesField
          value={assignees}
          options={roster}
          loading={assigneesApi.loading}
          onChange={setAssignees}
          disabled={pending}
        />

        <div className="row">
          <label className="field">
            <span className="label">Priority</span>
            <select value={priorityId} onChange={(e) => setPriorityId(e.target.value)} disabled={priorities.loading}>
              <option value="">—</option>
              {priorities.list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Department</span>
            <select value={department} onChange={(e) => setDepartment(e.target.value)} disabled={departmentsApi.loading}>
              <option value="">—</option>
              {departmentOptions.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={categoriesApi.loading}>
              <option value="">—</option>
              {categoryOptions.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Board (Kanban) view — action items grouped into status columns. Drag a card
// onto a column to change its status (PATCH status_code via updateTask). The
// per-card <select> is the keyboard-accessible fallback. Columns come from the
// tenant's configurable task statuses (0061), falling back to TASK_STATUSES.
// Reuses the project board's `.board-*` styles.
// ===========================================================================

/** Resolve a task's status code (denormalized code first, then the raw column). */
function statusCodeOf(t: TaskRow): string {
  return t.status_code || t.status || TASK_STATUSES[0]!.code;
}

function ActionItemsBoard({
  items,
  priorities,
  onOpen,
  onChanged,
}: {
  items: TaskRow[];
  priorities: PriorityIndex;
  onOpen: (id: string) => void;
  onChanged: () => void;
}) {
  const statusesApi = useApi((signal) => listTaskStatuses(signal), []);
  const columns: { code: string; name: string; color?: string | null }[] = useMemo(() => {
    const rows = statusesApi.data?.task_statuses;
    if (rows?.length) {
      return [...rows]
        .sort((a, b) => a.position - b.position)
        .map((s) => ({ code: s.code, name: s.name, color: s.color }));
    }
    return TASK_STATUSES.map((s) => ({ code: s.code, name: s.label }));
  }, [statusesApi.data]);

  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const moveMut = useMutation((vars: { id: string; status_code: string }) =>
    updateTask(vars.id, { status_code: vars.status_code }),
  );

  const handleDrop = useCallback(
    async (taskId: string, statusCode: string, currentStatus: string) => {
      setDragId(null);
      if (statusCode === currentStatus) return;
      setMovingId(taskId);
      await moveMut.run({ id: taskId, status_code: statusCode });
      setMovingId(null);
      onChanged();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveMut.run, onChanged],
  );

  const byStatus = (code: string) => items.filter((t) => statusCodeOf(t) === code);
  // Any item whose status isn't one of the known columns lands in the first.
  const known = new Set(columns.map((c) => c.code));
  const orphanCount = items.filter((t) => !known.has(statusCodeOf(t))).length;

  if (items.length === 0) {
    return (
      <div style={{ padding: 18 }}>
        <EmptyState>No action items yet. Create one and assign it to a teammate.</EmptyState>
      </div>
    );
  }

  return (
    <>
      {moveMut.error ? (
        <Alert kind="error">Couldn't move the action item: {moveMut.error}</Alert>
      ) : null}
      <div className="board">
        {columns.map((col, idx) => (
          <ActionItemColumn
            key={col.code}
            column={col}
            priorities={priorities}
            items={
              idx === 0
                ? [...byStatus(col.code), ...items.filter((t) => !known.has(statusCodeOf(t)))]
                : byStatus(col.code)
            }
            dragId={dragId}
            movingId={movingId}
            onDragState={setDragId}
            onDrop={handleDrop}
            onOpen={onOpen}
            note={idx === 0 && orphanCount > 0 ? `Includes ${orphanCount} with other statuses` : null}
          />
        ))}
      </div>
    </>
  );
}

function ActionItemColumn({
  column,
  priorities,
  items,
  dragId,
  movingId,
  onDragState,
  onDrop,
  onOpen,
  note,
}: {
  column: { code: string; name: string; color?: string | null };
  priorities: PriorityIndex;
  items: TaskRow[];
  dragId: string | null;
  movingId: string | null;
  onDragState: (id: string | null) => void;
  onDrop: (taskId: string, statusCode: string, currentStatus: string) => void;
  onOpen: (id: string) => void;
  note: string | null;
}) {
  const [dropOver, setDropOver] = useState(false);
  const depth = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current += 1;
    setDropOver(true);
  }
  function handleDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDropOver(false);
  }
  function handleDragOver(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current = 0;
    setDropOver(false);
    const payload = e.dataTransfer.getData("text/plain");
    if (!payload) return;
    const [taskId, currentStatus] = payload.split("|");
    if (taskId) onDrop(taskId, column.code, currentStatus ?? "");
  }

  // Stable accent per status code so each column reads distinctly.
  const accents = ["var(--st-open)", "var(--st-progress)", "var(--st-done)", "var(--accent)", "var(--brand-2)"];
  let h = 0;
  for (let i = 0; i < column.code.length; i++) h = (h * 31 + column.code.charCodeAt(i)) >>> 0;
  const colAccent = column.color || accents[h % accents.length];

  return (
    <section
      className={`board-col${dropOver ? " col-dropover" : ""}`}
      aria-label={column.name}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className="board-col-head">
        <span className="col-swatch" style={{ ["--col-accent" as string]: colAccent }} aria-hidden="true" />
        <span className="col-name">{column.name}</span>
        <span className="grow" />
        <span className="count">{items.length}</span>
      </header>
      {note ? <div className="board-col-wip">{note}</div> : null}
      <div className="board-col-body">
        {items.length === 0 ? (
          <p className="muted board-empty">No action items</p>
        ) : (
          items.map((t) => (
            <ActionItemCard
              key={t.id}
              item={t}
              current={statusCodeOf(t)}
              priorityCode={priorities.byId(t.priority_id)?.code ?? null}
              dragging={dragId === t.id}
              moving={movingId === t.id}
              onDragState={onDragState}
              onOpen={() => onOpen(t.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function ActionItemCard({
  item,
  current,
  priorityCode,
  dragging,
  moving,
  onDragState,
  onOpen,
}: {
  item: TaskRow;
  current: string;
  priorityCode: string | null;
  dragging: boolean;
  moving: boolean;
  onDragState: (id: string | null) => void;
  onOpen: () => void;
}) {
  const rail =
    {
      urgent: "var(--st-blocked)",
      high: "var(--st-progress)",
      normal: "var(--st-open)",
      low: "var(--st-closed)",
    }[(priorityCode ?? "").toLowerCase()] ?? "var(--st-closed)";

  const readOnly = item.can_edit === false;

  return (
    <article
      className={`board-card${moving ? " busy" : ""}${dragging ? " card-dragging" : ""}`}
      style={{ ["--card-rail" as string]: rail }}
      draggable={!readOnly}
      onDragStart={(e) => {
        // Read-only shared viewers can't change status — block the drag.
        if (readOnly) {
          e.preventDefault();
          return;
        }
        // Don't start a drag when the gesture begins on an interactive control
        // (the status <select>, the title button) — a native <select> inside a
        // draggable element otherwise never opens / never fires change.
        if ((e.target as HTMLElement).closest("select, button, input, a, option")) {
          e.preventDefault();
          return;
        }
        // Pack id + current status so the column knows whether the drop is a no-op.
        e.dataTransfer.setData("text/plain", `${item.id}|${current}`);
        e.dataTransfer.effectAllowed = "move";
        onDragState(item.id);
      }}
      onDragEnd={() => onDragState(null)}
    >
      {item.due_date ? (
        <span className="board-card-origin mono faint">{formatDate(item.due_date)}</span>
      ) : null}
      <button className="board-card-title" onClick={onOpen} title="Open action item">
        {item.title}
      </button>
      {readOnly ? (
        <span className="pill" title="Shared with you — read-only" style={{ alignSelf: "flex-start", marginTop: 2 }}>
          Read-only
        </span>
      ) : null}
      <div className="board-card-foot">
        {priorityCode ? <PriorityBadge code={priorityCode} /> : null}
        <span className="grow" />
        {item.created_by_name ? (
          <span className="faint" style={{ fontSize: "0.72rem" }}>{item.created_by_name}</span>
        ) : null}
        <Avatar name={item.assignee_name ?? item.created_by_name ?? undefined} size="sm" />
      </div>
    </article>
  );
}
